import * as React from 'react';
import { debounce } from 'lodash';
import swal from 'sweetalert';
import { message, Pagination } from 'components';
import { isBrokerBayAdminEmail, SUPPORT_EMAIL } from 'utils';
import { AuthContextUser } from 'contexts';
import { deleteUser, getOrgUsers } from 'api/UserController';
import { IMLSNumbers, UserRoles } from '@bb/users';
import PanelHeader from '../../../../app/react-common/PanelHeader';
import PanelBody from '../../../../app/react-common/PanelBody';
import Loading from '../../../../app/react-common/Loading';

type FetchedUser = {
  id: string;
  lastName: string;
  firstName: string;
  role: UserRoles;
  address: string;
  location: string;
  email: string;
  avatarFilestackId?: string;
  disabled?: boolean;
  isUnverified?: boolean;
  orgName?: string;
  branchName?: string;
  mls_numbers?: IMLSNumbers[];
};

interface ManageStaffListState {
  userDataLoaded: boolean;
  // TODO: Add user interface
  users: Array<FetchedUser>;
  page: number;
  totalItems?: number;
  searchName: string;
}

interface ManageStaffListProps {
  fetchUserCounts: () => Promise<void>;
  auth: AuthContextUser;
  active?: number;
  deleted?: number;
}

interface QueryOptions {
  name?: string;
}

class ManageStaffList extends React.Component<
  ManageStaffListProps,
  ManageStaffListState
> {
  constructor(props: ManageStaffListProps) {
    super(props);

    this.state = {
      userDataLoaded: false,
      users: [],
      page: 1,
      searchName: '',
    };
  }

  fetchUserData = (page: number, queryOptions?: QueryOptions) => {
    this.setState({ userDataLoaded: false }, async () => {
      // We have to use a callback here because TypeScript does not play nice
      // with promisify overloads
      const pageNumber = page - 1; // Go from 1-indexed page to 0-indexed pageNumber
      const { name: userName } = queryOptions || ({} as QueryOptions);
      const query = {
        pageNumber,
        userName,
      };
      const { users, count } = await getOrgUsers(query);

      this.setState({
        users,
        totalItems: count,

        userDataLoaded: true,
      });
    });
  };

  debouncedFetchUserData = debounce((page, query) => {
    this.fetchUserData(page, query);
  }, 500);

  changePage = (page: number) => {
    const { searchName } = this.state;
    this.setState({ page });
    this.fetchUserData(page, { name: searchName });
  };

  onUserModify = (
    event: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>,
    email: string,
  ) => {
    const { auth } = this.props;
    if (
      isBrokerBayAdminEmail(email) &&
      !auth?.isBrokerBay &&
      !auth.assumingUserId &&
      !auth.isAssumedNotBB
    ) {
      event.preventDefault();
      event.stopPropagation();
      message.error(
        `You cannot modify this BrokerBady Admin account. Please contact ${SUPPORT_EMAIL} for assistance.`,
      );
    }
  };

  deleteUser = (id: string) => {
    swal(
      {
        title: 'Delete User',
        text: 'Are you sure you want to delete this user?\nThis operation is permanent and cannot be undone.',
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#DD6B55',
        confirmButtonText: 'Delete',
        closeOnConfirm: true,
      },
      () => {
        this.setState({ userDataLoaded: false }, () => {
          // We have to use a callback here because TypeScript does not play nice
          // with promisify overloads
          const { fetchUserCounts } = this.props;
          // Angular doesn't play nice with async/await so we use promise chaining
          // NN-71 This .then is kept to keep angular way of swal behaviour when migrating from angularjs
          deleteUser({ userId: id })
            .then(() => {
              this.fetchUserData(this.state.page, {
                name: this.state.searchName,
              });
              fetchUserCounts();
              message.success('Delete was successful');
            })
            .catch((err: Error) => {
              message.error('Error deleting user');
              this.setState({ userDataLoaded: true });
            });
        });
      },
    );
  };

  onSearchNameChange = (e: React.ChangeEvent) => {
    const { page } = this.state;
    const target = e.target as HTMLInputElement;
    this.setState({ searchName: target.value });

    this.debouncedFetchUserData(page, { name: target.value });
  };

  componentDidMount() {
    const { page } = this.state;
    this.fetchUserData(page);
  }

  public render() {
    const { page, searchName, totalItems, userDataLoaded, users } = this.state;
    const { active, deleted } = this.props;

    const headerItemStyle = {
      marginRight: '10px',
      marginTop: '5px',
    };

    return (
      <>
        <PanelHeader
          title="Manage Staff"
          extra={[
            // Search input
            <div key="search-input" style={headerItemStyle}>
              <input
                type="text"
                className="form-control"
                placeholder="Search Staff"
                value={searchName}
                onChange={this.onSearchNameChange}
              />
            </div>,
            // New User button
            <div key="new-user" style={headerItemStyle}>
              <a href="/#/users/new" className="btn btn-primary">
                New User
              </a>
            </div>,
          ]}
        />

        {/* Tabs */}
        <nav className="content animate-panel tabs">
          <ul className="nav nav-tabs">
            <li key="active" className="active">
              <a href="/#/answering/manage-staff">
                Active
                {typeof active === 'number' && ` - ${active}`}
              </a>
            </li>
            <li key="deleted">
              <a href="/#/answering/manage-deleted-staff">
                Deleted
                {typeof deleted === 'number' && ` - ${deleted}`}
              </a>
            </li>
          </ul>
        </nav>

        <PanelBody>
          <div className="manage-users-list-body">
            {/* Users list */}
            {userDataLoaded && users.length !== 0 && (
              <table className="table table-striped table-bordered table-hover">
                <thead>
                  <tr>
                    <th>First Name</th>
                    <th>Last Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {users.map(user => (
                    <tr key={user.id}>
                      <td>{user.firstName}</td>
                      <td>{user.lastName}</td>
                      <td>{user.email}</td>
                      <td>{user.role}</td>
                      <td
                        onClickCapture={e => this.onUserModify(e, user.email)}
                      >
                        {/*
                          Intentionally made as onClick since above onClickCapture will
                          disable propagation if not permitted
                         */}
                        <a
                          onClick={() => {
                            window.location.href = `/#/users/edit/${user.id}`;
                          }}
                        >
                          Edit
                        </a>
                        {' | '}
                        <a onClick={() => this.deleteUser(user.id)}>Delete</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {users.length !== 0 && (
              <Pagination
                total={totalItems}
                defaultPageSize={20}
                current={page}
                showSizeChanger={false}
                onChange={this.changePage}
              />
            )}
            {/* No Users found */}
            {userDataLoaded && users.length === 0 && (
              <div className="panel-body">
                <label>No users found.</label>
              </div>
            )}

            {/* Loading */}
            {!userDataLoaded && <Loading size="fa-5x" />}
          </div>
        </PanelBody>
      </>
    );
  }
}

export default ManageStaffList;
